import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { orgAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import {
  PageHeader, Btn, Badge, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle, TempPasswordBox,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function OrgBranchesPage() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const orgId      = user?.organisationId;

  const [branches,    setBranches]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showAdd,     setShowAdd]     = useState(false);
  const [confirm,     setConfirm]     = useState(null);
  const [actionLoad,  setActionLoad]  = useState(false);
  const [result,      setResult]      = useState(null);

  const load = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    orgAdmin.getBranches(orgId)
      .then(r => setBranches(r.data?.results || r.data || []))
      .catch(() => toast.error("Failed to load branches."))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async () => {
    if (!confirm) return;
    setActionLoad(true);
    try {
      if (confirm.type === "suspend") await orgAdmin.suspendBranch(confirm.branch.id);
      else                             await orgAdmin.activateBranch(confirm.branch.id);
      toast.success(`${confirm.branch.name} ${confirm.type === "suspend" ? "suspended" : "activated"}.`);
      load();
    } catch { toast.error("Action failed."); }
    finally  { setActionLoad(false); setConfirm(null); }
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Branches"
        subtitle={`${branches.length} branch${branches.length !== 1 ? "es" : ""} in your organisation`}
        action={<Btn onClick={() => setShowAdd(true)}>+ Add Branch</Btn>}
      />

      {loading ? <LoadingSpinner /> : branches.length === 0 ? (
        <EmptyState icon="🏢" title="No branches yet"
          subtitle="Add your first branch to get started."
          action={<Btn onClick={() => setShowAdd(true)}>+ Add Branch</Btn>}
        />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead><tr>
              {["Branch","Admin","Status","Tickets Today","Waiting","Completion","Actions"].map(h =>
                <th key={h} style={styles.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {branches.map(b => (
                <tr key={b.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.name}>{b.name}</div>
                    <div style={styles.slug}>/{b.slug}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontSize: "13px", color: b.admin_name ? "var(--text-secondary)" : "#EF4444" }}>
                      {b.admin_name || "No admin"}
                    </span>
                  </td>
                  <td style={styles.td}><Badge value={b.status} /></td>
                  <td style={{ ...styles.td, textAlign: "center", color: "var(--text-secondary)" }}>{b.tickets_today ?? 0}</td>
                  <td style={{ ...styles.td, textAlign: "center", color: "#EAB308" }}>{b.waiting_now ?? 0}</td>
                  <td style={{ ...styles.td, textAlign: "center", color: "#22C55E" }}>
                    {b.completion_rate != null ? `${b.completion_rate}%` : "—"}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <Btn size="sm" onClick={() => navigate(`/org/branches/${b.id}/manage`)}>Manage</Btn>
                      {b.status === "suspended"
                        ? <Btn size="sm" variant="success" onClick={() => setConfirm({ type: "activate", branch: b })}>Activate</Btn>
                        : <Btn size="sm" variant="warning" onClick={() => setConfirm({ type: "suspend",  branch: b })}>Suspend</Btn>
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddBranchModal
        open={showAdd}
        orgId={orgId}
        onClose={() => setShowAdd(false)}
        onCreated={(data) => { setShowAdd(false); setResult(data); load(); }}
      />

      {/* Result modal — shows temp password */}
      <Modal open={!!result} onClose={() => setResult(null)} title="Branch Created 🎉" width="480px">
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "12px", padding: "16px", display: "flex", gap: "12px", alignItems: "center" }}>
              <span style={{ fontSize: "28px" }}>🏢</span>
              <div>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "#22C55E" }}>{result.name}</div>
                <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>/{result.slug}</div>
              </div>
            </div>
            {result.temp_password && (
              <TempPasswordBox password={result.temp_password} email={result.admin_user?.email} />
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <Btn variant="ghost" onClick={() => setResult(null)}>Close</Btn>
              <Btn onClick={() => { setResult(null); navigate(`/org/branches/${result.id}/manage`); }}>Manage Branch →</Btn>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!confirm} onClose={() => setConfirm(null)}
        onConfirm={handleAction} loading={actionLoad}
        title={confirm?.type === "suspend" ? "Suspend Branch?" : "Activate Branch?"}
        message={confirm?.type === "suspend"
          ? `${confirm?.branch?.name} will be suspended. Staff and patients lose access.`
          : `${confirm?.branch?.name} will be reactivated.`}
        confirmLabel={confirm?.type === "suspend" ? "Suspend" : "Activate"}
        variant={confirm?.type === "suspend" ? "danger" : "success"}
      />
    </div>
  );
}

function AddBranchModal({ open, orgId, onClose, onCreated }) {
  const [form,   setForm]   = useState({ name: "", slug: "", email: "", phone: "", address: "", admin_name: "", admin_email: "", admin_password: "", send_invite: true, password_mode: "auto" });
  const [saving, setSaving] = useState(false);
  const [step,   setStep]   = useState(0);

  useEffect(() => { if (open) { setForm({ name:"",slug:"",email:"",phone:"",address:"",admin_name:"",admin_email:"",admin_password:"",send_invite:true,password_mode:"auto" }); setStep(0); } }, [open]);

  const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const slug  = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const submit = async () => {
    if (!form.name.trim() || !form.slug.trim()) { toast.error("Name and slug required."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, slug: form.slug, email: form.email,
        phone: form.phone, address: form.address,
        admin_name: form.admin_name || undefined,
        admin_email: form.admin_email || undefined,
        admin_password: form.password_mode === "manual" ? form.admin_password : undefined,
        send_invite: form.send_invite,
      };
      const { data } = await orgAdmin.addBranch(orgId, payload);
      toast.success(`${form.name} created!`);
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.slug?.[0] || e.response?.data?.detail || "Failed to create branch.");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add New Branch" width="520px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {step === 0 && <>
          <FormField label="Branch Name" required>
            <input style={inputStyle} value={form.name} onChange={e => { set("name", e.target.value); if (!form.slug) set("slug", slug(e.target.value)); }} placeholder="e.g. Accra Main Branch" />
          </FormField>
          <FormField label="Slug (URL Identifier)" required hint={`Kiosk: /kiosk/${form.slug || "slug"}`}>
            <input style={inputStyle} value={form.slug} onChange={e => set("slug", slug(e.target.value))} placeholder="accra-main-branch" />
          </FormField>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <FormField label="Email"><input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="branch@org.com" /></FormField>
            <FormField label="Phone"><input style={inputStyle} value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="030 XXX XXXX" /></FormField>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)" }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={() => setStep(1)} disabled={!form.name.trim() || !form.slug.trim()}>Next: Branch Admin →</Btn>
          </div>
        </>}

        {step === 1 && <>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", background: "rgba(37,99,235,0.06)", borderRadius: "8px", padding: "10px 12px", margin: 0 }}>
            Optionally create a branch admin. Leave email blank to manage this branch directly.
          </p>
          <FormField label="Admin Full Name"><input style={inputStyle} value={form.admin_name} onChange={e => set("admin_name", e.target.value)} placeholder="e.g. Kwame Mensah" /></FormField>
          <FormField label="Admin Email"><input style={inputStyle} type="email" value={form.admin_email} onChange={e => set("admin_email", e.target.value)} placeholder="admin@branch.com" /></FormField>
          {form.admin_email && (
            <FormField label="Password">
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text-primary)" }}><input type="radio" checked={form.password_mode === "auto"} onChange={() => set("password_mode", "auto")} />Auto-generate temp password</label>
                <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text-primary)" }}><input type="radio" checked={form.password_mode === "manual"} onChange={() => set("password_mode", "manual")} />Set manually</label>
              </div>
            </FormField>
          )}
          {form.admin_email && form.password_mode === "manual" && (
            <FormField label="Password"><input style={inputStyle} type="password" value={form.admin_password} onChange={e => set("admin_password", e.target.value)} placeholder="Min. 8 characters" /></FormField>
          )}
          {form.admin_email && (
            <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--text-secondary)" }}>
              <input type="checkbox" checked={form.send_invite} onChange={e => set("send_invite", e.target.checked)} />
              Send welcome email with credentials
            </label>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)" }}>
            <Btn variant="ghost" onClick={() => setStep(0)}>← Back</Btn>
            <Btn onClick={submit} disabled={saving}>{saving ? "Creating…" : "Create Branch"}</Btn>
          </div>
        </>}
      </div>
    </Modal>
  );
}

const styles = {
  root:     { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1200px" },
  tableWrap:{ background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px", overflow: "hidden" },
  table:    { width: "100%", borderCollapse: "collapse" },
  th:       { padding: "12px 16px", fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.07em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid var(--surface-border)", background: "var(--table-header-bg)" },
  tr:       { borderBottom: "1px solid rgba(37,99,235,0.06)" },
  td:       { padding: "13px 16px", verticalAlign: "middle" },
  name:     { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" },
  slug:     { fontSize: "11px", color: "var(--text-faint)", fontFamily: "monospace" },
};
